import { ref } from 'vue';

/**
 * The one dialog that stands between a tap and any spend — credits, seals or
 * research XP. Every purchase, research buy and XP conversion asks here
 * instead of `window.confirm`, so the sheet is themed once (`ui/ConfirmSheet.vue`)
 * and every call site gets it for the price of an `await`.
 *
 * A price line names an amount and which ledger it leaves — the wording is the
 * sheet's job, not the caller's, which is why this module carries no strings.
 */
export interface ConfirmPrice {
  amount: number;
  currency: 'credits' | 'diamonds' | 'xp';
}

export interface ConfirmRequest {
  title: string;
  body?: string;
  price?: ConfirmPrice[];
  /** Defaults to the sheet's own "Buy" caption when left out. */
  confirmLabel?: string;
  /** An image url for the item being bought — optional, most callers have none. */
  art?: string;
  /** Red plate, for a spend that cannot be undone. Off (yellow or seal) by default. */
  danger?: boolean;
}

/**
 * The one request on screen, or none. A module-scope ref rather than a prop:
 * the sheet is mounted once near the root (`App.vue`, beside `ConfirmSignOut`)
 * and every screen that spends something reaches it by importing this file,
 * the same shape `confirm-sign-out.ts` uses for Sign out.
 */
const request = ref<ConfirmRequest | null>(null);

/** Read by `ui/ConfirmSheet.vue`. */
export const confirmRequest = request;

/** The pending promise's own resolver, so a second ask can settle the first. */
let settlePending: ((ok: boolean) => void) | null = null;

function settle(ok: boolean): void {
  const resolve = settlePending;
  settlePending = null;
  request.value = null;
  resolve?.(ok);
}

/**
 * Opens the sheet and resolves once the player answers.
 *
 * A second call while one is still open decides the first as `false` before
 * opening the second — the same "nothing queues" rule `askSignOut` follows,
 * so a screen that fires two asks in a row never leaves an orphaned promise
 * waiting on an answer nobody can see.
 */
export function askToConfirm(req: ConfirmRequest): Promise<boolean> {
  settle(false);
  return new Promise<boolean>((resolve) => {
    settlePending = resolve;
    request.value = req;
  });
}

/** Called by the confirm plate. */
export function confirmYes(): void {
  settle(true);
}

/** Called by Cancel, the backdrop and Escape — the safe answer stays the easy one. */
export function confirmNo(): void {
  settle(false);
}
